import React from "react";
import Link from "next/link";
import { useDispatch, useSelector } from "react-redux";
import { addItem, addToCartAction } from "@/redux/slices/cartSlice";
import { AppDispatch, RootState } from "@/redux/store";
import { useRouter } from "next/navigation";
import { getEffectivePrice } from "@/utils/priceUtils";

interface ProductCardProps {
  id: string | number;
  url?: string;
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  emoji?: string;
  image?: string;
  badge?: string;
  gstPer?: number;
  minimumQty?: number | null;
  isCostToCost?: boolean;
  isOthersProduct?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  id,
  url,
  name,
  category,
  price,
  originalPrice,
  emoji,
  image,
  badge,
  gstPer,
  minimumQty,
  isCostToCost,
  isOthersProduct,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { isCustomerAuthenticated, user } = useSelector(
    (state: RootState) => state.auth,
  );
  const { isInternational } = useSelector(
    (state: RootState) => state.location,
  );
  const effectivePrice = getEffectivePrice(price, isInternational);
  const isFirstOrderEligible =
    !isCustomerAuthenticated || (isCustomerAuthenticated && user?.isFirstOrder);
  const isCostToCostProduct = isCostToCost;
  const hasFirstOrderDiscount =
    !isCostToCostProduct && isFirstOrderEligible && effectivePrice >= 3000;

  const discountedPrice = hasFirstOrderDiscount ? effectivePrice * 0.95 : effectivePrice;
  const showStrikethrough = hasFirstOrderDiscount;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    router.push(`/product/${url || id}`);
  };

  return (
    <Link
      href={`/product/${url || id}`}
      className="group relative bg-black-2 border-b border-r border-border-1 hover:bg-black-3 transition-colors duration-300"
    >
      <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-gold-core to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

      <div className="h-44 bg-gradient-to-br from-black-2 to-black-3 flex items-center justify-center text-4xl relative overflow-hidden">
        <div className="absolute bottom-0 inset-x-0 h-2/5 bg-gradient-to-t from-gold-dim/5 to-transparent" />
        {badge && (
          <div className="absolute top-2.5 left-2.5 bg-gradient-to-r from-gold-dim to-gold-core text-white text-[8px] font-extrabold tracking-widest px-2 py-0.5 rounded-sm z-10 shadow-sm">
            {badge.toUpperCase()}
          </div>
        )}
        {/* {isOthersProduct && (
          <div className="absolute top-2.5 right-2.5 bg-blue-600/60 backdrop-blur-md border border-blue-400/30 text-white text-[8px] font-bold tracking-widest px-2 py-0.5 rounded-sm z-10 uppercase">
            Other Product
          </div>
        )} */}
        {minimumQty && minimumQty > 1 && (
          <div className="absolute top-2.5 right-2.5 bg-black/60 backdrop-blur-md border border-border-1 text-gold-core text-[8px] font-bold tracking-widest px-2 py-0.5 rounded-sm z-10 shadow-sm">
            MIN. QTY: {minimumQty}
          </div>
        )}
        {image ? (
          <img
            src={image}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <span className="group-hover:scale-110 transition-transform duration-500">
            {emoji}
          </span>
        )}
      </div>

      <div className="p-4.5">
        <div className="text-[9px] text-gold-core tracking-[0.14em] uppercase mb-1">
          {category}
        </div>
        <h3 className="text-sm text-text-1 font-semibold mb-3 line-clamp-2 leading-relaxed min-h-[40px] uppercase">
          {name}
        </h3>

        <div className="flex items-baseline gap-2 mb-1.5">
          {showStrikethrough ? (
            <>
              <span className="text-[11px] text-text-3 line-through">
                ₹{effectivePrice.toFixed(0)}
              </span>
              <span className="font-cormorant text-2xl font-bold gold-text-gradient">
                ₹{discountedPrice.toFixed(0)}
              </span>
            </>
          ) : (
            <span className="font-cormorant text-2xl font-bold gold-text-gradient">
              ₹{effectivePrice.toFixed(0)}
            </span>
          )}
        </div>

        <div className="text-[9px] text-gold-dim/80 mb-3 tracking-wide uppercase">
          {!isCostToCost && (
            <>
              {showStrikethrough ? "5% Member Price" : "Wholesale Price"}
              {isFirstOrderEligible &&
                !hasFirstOrderDiscount &&
                " · Login Price -5%"}
            </>
          )}
          {isCostToCost && "Direct Factory Price"}
        </div>

        <button
          className="w-full py-2 border border-border-1 text-[10px] text-text-3 font-medium tracking-[0.12em] uppercase transition-all bg-gold-core hover:border-transparent text-white focus:bg-gold-core focus:border-transparent focus:text-white focus:outline-none disabled:opacity-20 disabled:cursor-not-allowed cursor-pointer hover:scale-105 focus:scale-105"
          onClick={handleAddToCart}
          disabled={!id || !name || price === undefined}
        >
          {!id || !name || price === undefined ? "Out of Stock" : "Add to Cart"}
        </button>
      </div>
    </Link>
  );
};
